import { ODSCheckbox } from 'kfone-component-library';
import type { RtiCustomFieldToggle, RtiFieldLeaf, RtiFieldSection } from 'model/interfaces/rti-employee-data-fields';
import {
  RTI_FIELD_HINT_KEYS,
  getRtiFieldLabel,
  isCustomFieldValue,
} from 'utilities/rti-employee-data-fields';
import {
  getAggregatedCompensationFieldOrder,
  isRtiAggregatedCustomCompFieldKey,
} from 'utilities/rti-aggregated-compensation';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type RtiAggregatedCompensationProps = {
  value: RtiFieldSection;
  onChange: (next: RtiFieldSection) => void;
  disabled?: boolean;
};

function ColumnHeader({ children }: { children: ReactNode }) {
  return (
    <p className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal uppercase text-[#5D5C5C]'>
      {children}
    </p>
  );
}

function FieldRowBoolean(props: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}) {
  const { id, label, checked, onChange, description } = props;
  return (
    <div className='flex flex-col gap-1'>
      <ODSCheckbox
        id={id}
        checkList={[
          {
            label,
            value: id,
            checked,
          },
        ]}
        onChange={(checkbox) => {
          onChange(checkbox?.checked ?? false);
        }}
      />
      {description ? (
        <p className='ml-8 font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#747373]'>
          {description}
        </p>
      ) : null}
    </div>
  );
}

/** API-driven labeled toggle (e.g. 12-month LTI/STI): show `label` from payload, no column editor. */
function FieldRowLabeledToggle(props: {
  id: string;
  fieldKey: string;
  data: RtiCustomFieldToggle;
  value: RtiFieldSection;
  onChange: (next: RtiFieldSection) => void;
}) {
  const { t } = useTranslation();
  const { id, fieldKey, data, value, onChange } = props;

  const setEnabled = useCallback(
    (checked: boolean) => {
      onChange({
        ...value,
        [fieldKey]: { ...data, enabled: checked },
      });
    },
    [data, fieldKey, onChange, value],
  );

  const checkboxLabel = getRtiFieldLabel(fieldKey, t);

  return (
    <FieldRowBoolean
      id={`${id}-en`}
      label={checkboxLabel}
      checked={data.enabled}
      onChange={setEnabled}
    />
  );
}

function FieldRowCustom(props: {
  id: string;
  fieldKey: string;
  data: RtiCustomFieldToggle;
  value: RtiFieldSection;
  onChange: (next: RtiFieldSection) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { id, fieldKey, data, value, onChange, disabled } = props;

  const checkboxLabel = getRtiFieldLabel(fieldKey, t);

  const patch = useCallback(
    (nextLeaf: RtiCustomFieldToggle) => {
      onChange({
        ...value,
        [fieldKey]: nextLeaf,
      });
    },
    [fieldKey, onChange, value],
  );

  const setEnabled = useCallback(
    (checked: boolean) => {
      patch({ ...data, enabled: checked });
    },
    [data, patch],
  );

  const setLabel = useCallback(
    (label: string) => {
      patch({ ...data, label });
    },
    [data, patch],
  );

  return (
    <div className='flex flex-col gap-2'>
      <FieldRowBoolean
        id={`${id}-en`}
        label={checkboxLabel}
        checked={data.enabled}
        onChange={setEnabled}
      />
      <input
        type='text'
        className='ml-8 w-full max-w-[220px] rounded border border-[#C5C7CA] px-2 py-1.5 font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-[#1D1D1D] placeholder:text-[#6C6F75] disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:opacity-60'
        value={data.label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={checkboxLabel}
        disabled={disabled || !data.enabled}
        aria-label={t('pe.settings.rti.employeeData.custom.labelAria')}
      />
    </div>
  );
}

export function RtiAggregatedCompensation({ value, onChange, disabled }: RtiAggregatedCompensationProps) {
  const { t } = useTranslation();

  const displayFieldKeys = useMemo(() => getAggregatedCompensationFieldOrder(value), [value]);

  const updateLeaf = useCallback(
    (fieldKey: string, leaf: RtiFieldLeaf) => {
      onChange({
        ...value,
        [fieldKey]: leaf,
      });
    },
    [onChange, value],
  );

  const renderField = (fieldKey: string) => {
    const rowId = `rti-agg-comp-${fieldKey}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    const leaf = value[fieldKey];
    if (leaf === undefined) {
      return null;
    }
    const label = getRtiFieldLabel(fieldKey, t);

    if (typeof leaf === 'boolean') {
      const hintKey = RTI_FIELD_HINT_KEYS[fieldKey];
      return (
        <FieldRowBoolean
          key={fieldKey}
          id={rowId}
          label={label}
          checked={leaf}
          onChange={(checked) => updateLeaf(fieldKey, checked)}
          description={hintKey ? t(hintKey) : undefined}
        />
      );
    }
    if (isCustomFieldValue(leaf)) {
      if (isRtiAggregatedCustomCompFieldKey(fieldKey)) {
        return (
          <FieldRowCustom
            key={fieldKey}
            id={rowId}
            fieldKey={fieldKey}
            data={leaf}
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        );
      }
      return (
        <FieldRowLabeledToggle
          key={fieldKey}
          id={rowId}
          fieldKey={fieldKey}
          data={leaf}
          value={value}
          onChange={onChange}
        />
      );
    }
    return null;
  };

  return (
    <section className='flex flex-col gap-4' aria-labelledby='rti-aggregated-compensation-title'>
      <h3
        id='rti-aggregated-compensation-title'
        className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-bold text-[#1D1D1D]'
      >
        {t('pe.settings.rti.aggregatedCompensation.title')}
      </h3>
      <p className='-mt-2 font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#1D1D1D]'>
        {t('pe.settings.rti.aggregatedCompensation.helper')}
      </p>

      <div className={disabled ? 'flex min-w-0 flex-col gap-4 pointer-events-none opacity-60' : 'flex min-w-0 flex-col gap-4'}>
        <ColumnHeader>{t('pe.settings.rti.employeeData.category.compensation')}</ColumnHeader>
        <div className='flex max-w-xl flex-col gap-4'>
          {displayFieldKeys.map((key) => renderField(key))}
        </div>
      </div>
    </section>
  );
}
